import useFavouriteOrder from '@hooks/useFavouriteOrder'
import useFavouriteOrderItems from '@hooks/useFavouriteOrderItems'
import useFavouriteOrders from '@hooks/useFavouriteOrders'
import useSelector from '@hooks/useSelector'

import type {
  AgencyModel,
  FavouriteOrderItemModel,
  ProviderModel,
  ProviderPriceListItemModel,
  ProviderPriceListItemOptionModel,
  ServiceProductModel,
} from '@models/types'

type CreateFavouriteOrderParams = {
  createFn: ReturnType<typeof useFavouriteOrder>['callbacks']['createFavouriteOrder'],
  optionsString: string,
  orderTotal: number,
  provider: ProviderModel,
  providerPriceListItem: ProviderPriceListItemModel,
  providerPriceListItemOptions: ProviderPriceListItemOptionModel[],
  serviceProduct: ServiceProductModel,
}

const createFavouriteOrder = (params: CreateFavouriteOrderParams) => {
  const {
    createFn,
    optionsString,
    orderTotal,
    provider,
    providerPriceListItem,
    providerPriceListItemOptions,
    serviceProduct,
  } = params

  const favouriteOrder = {
    provider_id: provider.id,
    title: serviceProduct.title,
    total: orderTotal,
    order_type: 'item',
    items_attributes: [
      {
        base_price: orderTotal,
        options: JSON.stringify(providerPriceListItemOptions),
        options_string: optionsString,
        price: orderTotal,
        provider_id: provider.id,
        provider_price_list_id: providerPriceListItem.provider_price_list_id,
        provider_price_list_item_id: providerPriceListItem.id,
        quantity: providerPriceListItem.quantity,
        service_id: serviceProduct.service_id,
        service_product_id: serviceProduct.id,
        title: serviceProduct.title,
      },
    ],
  }

  return createFn(favouriteOrder, { include_favourite_order_items: true })
}

type UseFavouriteOrderButton = {
  agency: AgencyModel,
  favouriteOrderItem?: FavouriteOrderItemModel,
  filters: {
    agency_id: number,
    options_string: string,
    provider_price_list_item_id: number,
  },
  orderTotal: number,
  provider: ProviderModel,
  providerPriceListItem: ProviderPriceListItemModel,
  providerPriceListItemOptions: ProviderPriceListItemOptionModel[],
  serviceProduct: ServiceProductModel,
}

function useFavouriteOrderButton(options: UseFavouriteOrderButton) {
  const {
    agency,
    favouriteOrderItem,
    filters: {
      agency_id,
      options_string,
      provider_price_list_item_id,
    },
    orderTotal,
    provider,
    providerPriceListItem,
    providerPriceListItemOptions,
    serviceProduct,
  } = options

  const favouriteOrders = useSelector(reduxState => reduxState.entities.favouriteOrders)

  const favouriteOrdersPayload = useFavouriteOrders({
    filters: {
      agency_id,
    },
    performHttpRequests: !!agency_id,
    requestOptions: {
      include_favourite_order_items: true,
    },
  })

  const { loading: loadingFavouriteOrders } = favouriteOrdersPayload

  const favouriteOrderItemsPayload = useFavouriteOrderItems({
    filters: {
      options_string,
      provider_price_list_item_id,
    },
  })

  const { filteredFavouriteOrderItems } = favouriteOrderItemsPayload

  const matchedFavouriteItem = favouriteOrderItem || filteredFavouriteOrderItems.find((item) => {
    // @ts-expect-error TS2538
    const favouriteOrder = favouriteOrders[item.favourite_order_id]
    return !!favouriteOrder
  })

  const favouriteOrderPayload = useFavouriteOrder({
    agency_id: agency.id,
    id: matchedFavouriteItem?.favourite_order_id,
    order_type: 'item',
    provider_id: provider.id,
    title: serviceProduct.title,
    total: orderTotal,
  })

  const {
    callbacks: {
      createFavouriteOrder: createFn,
      deleteFavouriteOrder,
    },
    creating,
    deleting,
  } = favouriteOrderPayload

  return {
    callbacks: {
      createFavouriteOrder: () => createFavouriteOrder({
        createFn,
        optionsString: options_string,
        orderTotal,
        provider,
        providerPriceListItem,
        providerPriceListItemOptions,
        serviceProduct,
      }),
      deleteFavouriteOrder,
    },
    loading: creating || deleting,
    loadingFavouriteOrders,
    matchedFavouriteItem,
  }
}

export default useFavouriteOrderButton
